import { useState, useMemo } from "react"
import { useNavigate } from "react-router-dom"
import {
  Grid,
  Paper,
  Typography,
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemText,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Stack,
  Button,
  IconButton,
  Tooltip,
  LinearProgress,
  Avatar,
  Skeleton,
} from "@mui/material"
import {
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  CalendarToday as CalendarIcon,
  Refresh as RefreshIcon,
  Assessment as AssessmentIcon,
  Block as BlockIcon,
  Inbox as InboxIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material"
import { useApiQuery } from "../../hooks/useApi"
import ReportGenerator from "./ReportGenerator"
import NormalLiveActivityFeed from "../../components/activity/NormalLiveActivityFeed"

// Reusable Metric Card with SaaS styling and hover animations
function MetricCard({ title, value, icon, bgColor, iconColor, subtitle }) {
  return (
    <Card
      sx={{
        height: "100%",
        borderRadius: "12px",
        border: "1px solid #f1f5f9",
        // boxShadow: "0 1px 3px 0 rgba(15, 23, 42, 0.05), 0 1px 2px 0 rgba(15, 23, 42, 0.03)",
        // transition: "all 0.2s ease-in-out",
        backgroundColor: "#ffffff",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: "0 5px 10px -3px rgba(15, 23, 42, 0.08), 0 4px 6px -2px rgba(15, 23, 42, 0.04)",
        },
      }}
    >
      <CardContent sx={{ p: 2.5, "&:last-child": { pb: 2.5 } }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box>
            <Typography
              color="textSecondary"
              gutterBottom
              variant="overline"
              sx={{ fontWeight: 600, letterSpacing: "0.05em", color: "#64748b" }}
            >
              {title}
            </Typography>
            <Typography variant="h3" component="h2" sx={{ fontWeight: 700, color: "#0f172a", my: 0.5 }}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                {subtitle}
              </Typography>
            )}
          </Box>
          <Avatar
            sx={{
              backgroundColor: bgColor,
              color: iconColor,
              width: 52,
              height: 52,
              borderRadius: "10px",
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  )
}

// Visual Progress Row for the Job Pipeline
function PipelineProgressRow({ label, count, total, color, subStatusKey }) {
  const percentage = total > 0 ? Math.round((count / total) * 100) : 0
  const hasJobs = count > 0

  return (
    <Box sx={{ mb: 2, opacity: hasJobs ? 1 : 0.45, transition: "opacity 0.2s" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, color: "#334155" }}>
          {label}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2" sx={{ fontWeight: 600, color: "#1e293b" }}>
            {count}
          </Typography>
          <Typography variant="caption" sx={{ color: "#94a3b8" }}>
            ({percentage}%)
          </Typography>
        </Stack>
      </Box>
      {/* <LinearProgress
        variant="determinate"
        value={percentage}
        sx={{
          height: 8,
          borderRadius: "4px",
          backgroundColor: "#e2e8f0",
          "& .MuiLinearProgress-bar": {
            backgroundColor: color,
            borderRadius: "4px",
          },
        }}
      /> */}
    </Box>
  )
}

// Skeleton Loader to prevent layout shifts
function DashboardSkeleton() {
  return (
    <Box sx={{ py: 1 }}>
      <Box sx={{ display: "flex", flexDirection: { xs: "column", md: "row" }, justifyContent: "space-between", alignItems: { xs: "stretch", md: "center" }, mb: 4, gap: 2 }}>
        <Box>
          <Skeleton variant="text" width={220} height={40} />
          <Skeleton variant="text" width={320} height={20} />
        </Box>
        <Stack direction="row" spacing={2} sx={{ width: { xs: "100%", md: "auto" } }}>
          <Skeleton variant="rectangular" width={180} height={40} sx={{ borderRadius: "8px" }} />
          <Skeleton variant="rectangular" width={150} height={40} sx={{ borderRadius: "8px" }} />
        </Stack>
      </Box>

      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[1, 2, 3, 4].map((i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <Paper sx={{ p: 2.5, borderRadius: "12px", border: "1px solid #f1f5f9", boxShadow: "none" }}>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 2 }}>
                <Box sx={{ width: "60%" }}>
                  <Skeleton variant="text" width="90%" height={16} />
                  <Skeleton variant="text" width="60%" height={36} />
                </Box>
                <Skeleton variant="rectangular" width={52} height={52} sx={{ borderRadius: "10px" }} />
              </Box>
              <Skeleton variant="text" width="40%" height={16} />
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 3, borderRadius: "12px", border: "1px solid #f1f5f9", boxShadow: "none" }}>
            <Skeleton variant="text" width="30%" height={28} sx={{ mb: 3 }} />
            {[1, 2, 3].map((i) => (
              <Box key={i} sx={{ mb: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Skeleton variant="text" width="25%" height={20} />
                  <Skeleton variant="rectangular" width={90} height={20} sx={{ borderRadius: "10px" }} />
                </Box>
                <Skeleton variant="text" width="70%" height={16} />
                <Skeleton variant="text" width="40%" height={14} />
                {i < 3 && <Divider sx={{ mt: 2.5 }} />}
              </Box>
            ))}
          </Paper>
        </Grid>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3, borderRadius: "12px", border: "1px solid #f1f5f9", boxShadow: "none" }}>
            <Skeleton variant="text" width="50%" height={28} sx={{ mb: 3 }} />
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Box key={i} sx={{ mb: 2.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
                  <Skeleton variant="text" width="40%" height={18} />
                  <Skeleton variant="text" width="15%" height={18} />
                </Box>
                <Skeleton variant="rectangular" width="100%" height={8} sx={{ borderRadius: "4px" }} />
              </Box>
            ))}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  )
}

function NormalDashboard() {
  const navigate = useNavigate()
  const [dateFilter, setDateFilter] = useState("today")
  const [reportDialogOpen, setReportDialogOpen] = useState(false)

  const { data: userStats, isLoading, refetch } = useApiQuery(["user-stats"], "/users/stats")

  // Filter jobs based on date range selected
  const getFilteredStats = useMemo(() => {
    if (!userStats) return null

    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
    const monthStart = new Date(now.getFullYear(), now.getMonth(), 1)

    const filterByDate = (jobs) => {
      if (!jobs || !Array.isArray(jobs)) return []

      return jobs.filter((job) => {
        const jobDate = new Date(job.createdAt || job.dueDate)

        switch (dateFilter) {
          case "today":
            return jobDate >= today
          case "30days":
            return jobDate >= thirtyDaysAgo
          case "month":
            return jobDate >= monthStart
          case "all":
          default:
            return true
        }
      })
    }

    const filteredRecentJobs = filterByDate(userStats.recentJobs || [])

    // Recalculate states based on filter boundaries
    const newJobs = filterByDate(userStats.allJobs?.filter(j => j.status === "new") || []).length
    const completedJobs = filterByDate(userStats.allJobs?.filter(j => j.status === "completed") || []).length
    const cancelledJobs = filterByDate(userStats.allJobs?.filter(j => j.status === "cancelled") || []).length

    // Recalculate processing status counts
    const processingJobsFiltered = filterByDate(userStats.allJobs?.filter(j => j.status === "processing") || [])
    const processingJobs = {
      accepted: processingJobsFiltered.filter(j => j.subStatus === "accepted" || !j.subStatus).length,
      documentsPending: processingJobsFiltered.filter(j => j.subStatus === "documentsPending").length,
      taxPending: processingJobsFiltered.filter(j => j.subStatus === "taxPending").length,
      verificationPending: processingJobsFiltered.filter(j => j.subStatus === "verificationPending").length,
      clientNotResponding: processingJobsFiltered.filter(j => j.subStatus === "clientNotResponding").length,
      onHold: processingJobsFiltered.filter(j => j.subStatus === "keptOnHold").length,
    }

    return {
      newJobs,
      processingJobs,
      completedJobs,
      cancelledJobs,
      recentJobs: filteredRecentJobs.slice(0, 10),
    }
  }, [userStats, dateFilter])

  const displayStats = getFilteredStats || {
    newJobs: 0,
    processingJobs: {
      accepted: 0,
      documentsPending: 0,
      taxPending: 0,
      verificationPending: 0,
      clientNotResponding: 0,
      onHold: 0,
    },
    completedJobs: 0,
    cancelledJobs: 0,
    recentJobs: [],
  }

  const totalProcessing = Object.values(displayStats.processingJobs).reduce((sum, count) => sum + count, 0)

  const getStatusColor = (status) => {
    const statusColors = {
      "Documents Pending": "warning",
      "documentsPending": "warning",
      "Tax Pending": "info",
      "taxPending": "info",
      "Accepted": "success",
      "accepted": "success",
      "Verification Pending": "secondary",
      "verificationPending": "secondary",
      "Client Not Responding": "error",
      "clientNotResponding": "error",
      "Kept on Hold": "default",
      "keptOnHold": "default",
      "completed": "success",
      "processing": "primary",
      "new": "info",
      "cancelled": "error",
    }
    return statusColors[status] || "default"
  }

  const getFilterLabel = () => {
    switch (dateFilter) {
      case "today":
        return "Today"
      case "30days":
        return "Last 30 Days"
      case "month":
        return "This Month"
      case "all":
        return "All Time"
      default:
        return "This Month"
    }
  }

  const getSubStatusLabel = (subStatus) => {
    const labels = {
      accepted: "Accepted",
      documentsPending: "Documents Pending",
      taxPending: "Tax Pending",
      verificationPending: "Verification Pending",
      clientNotResponding: "Client Not Responding",
      keptOnHold: "Kept on Hold",
    }
    return labels[subStatus] || subStatus
  }

  if (isLoading) return <DashboardSkeleton />

  return (
    <Box sx={{ py: 1, minHeight: "90vh" }}>
      {/* Top Actionable Header Toolbar */}
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "stretch", md: "center" },
          gap: 2,
          mb: 4,
        }}
      >
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 800, color: "#0f172a", letterSpacing: "-0.02em" }}>
            Overview
          </Typography>
          <Typography variant="body2" sx={{ color: "#64748b", mt: 0.5 }}>
            Manage client operational workloads, check pipeline stages, and update records.
          </Typography>
        </Box>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={2}
          alignItems={{ xs: "stretch", sm: "center" }}
        >
          {/* Integrated Filter Control */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              backgroundColor: "#ffffff",
              border: "1px solid #e2e8f0",
              borderRadius: "8px",
              px: 1.5,
              height: 40,
              boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.02)",
            }}
          >
            <CalendarIcon sx={{ color: "#94a3b8", fontSize: 18, mr: 1 }} />
            <Typography variant="body2" sx={{ color: "#475569", fontWeight: 500, mr: 1.5, display: { xs: "none", sm: "inline" } }}>
              Filter:
            </Typography>
            <FormControl variant="standard" size="small">
              <Select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                disableUnderline
                sx={{
                  fontSize: "0.875rem",
                  fontWeight: 600,
                  color: "#1e293b",
                  cursor: "pointer",
                  "& .MuiSelect-select": {
                    py: 0,
                    pr: 3,
                  },
                }}
              >
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="30days">Last 30 Days</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="all">All Time</MenuItem>
              </Select>
            </FormControl>
          </Box>

          <Stack direction="row" spacing={1.5} sx={{ flexGrow: { xs: 1, sm: 0 } }}>
            <Tooltip title="Refresh data">
              <IconButton
                onClick={() => refetch()}
                sx={{
                  border: "1px solid #e2e8f0",
                  borderRadius: "8px",
                  backgroundColor: "#ffffff",
                  height: 40,
                  width: 40,
                  color: "#475569",
                  "&:hover": { backgroundColor: "#f1f5f9" },
                }}
              >
                <RefreshIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>

            <Button
              variant="contained"
              startIcon={<AssessmentIcon />}
              onClick={() => setReportDialogOpen(true)}
              sx={{
                backgroundColor: "#1976d2",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 600,
                px: 2.5,
                height: 40,
                boxShadow: "0 1px 2px 0 rgba(59, 130, 246, 0.3)",
                "&:hover": {
                  backgroundColor: "#1976d2",
                  boxShadow: "none",
                },
              }}
            >
              Generate Report
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* 2-COLUMN WORKSPACE LAYOUT */}
      <Grid container spacing={2.5}>
        {/* LEFT COLUMN: Metrics, Recent Activity & Job Pipeline */}
        <Grid item xs={12} md={7.5} lg={8}>
          {/* Metric Cards Area */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={6} sm={3}>
              <MetricCard
                title="New Jobs"
                value={displayStats.newJobs}
                icon={<AssignmentIcon sx={{ fontSize: 26 }} />}
                bgColor="#e0f2fe"
                iconColor="#0369a1"
                subtitle={`${getFilterLabel()} arrival`}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MetricCard
                title="Processing"
                value={totalProcessing}
                icon={<ScheduleIcon sx={{ fontSize: 26 }} />}
                bgColor="#fef3c7"
                iconColor="#b45309"
                subtitle="Operational workload"
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MetricCard
                title="Completed"
                value={displayStats.completedJobs}
                icon={<CheckCircleIcon sx={{ fontSize: 26 }} />}
                bgColor="#dcfce7"
                iconColor="#15803d"
                subtitle={`${getFilterLabel()} settlements`}
              />
            </Grid>
            <Grid item xs={6} sm={3}>
              <MetricCard
                title="Cancelled"
                value={displayStats.cancelledJobs}
                icon={<BlockIcon sx={{ fontSize: 26 }} />}
                bgColor="#fee2e2"
                iconColor="#b91c1c"
                subtitle="Rejected / terminated"
              />
            </Grid>
          </Grid>

          <Grid container spacing={2.5}>
            {/* Recent Jobs list */}
            <Grid item xs={12} sm={6}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: "12px",
                  border: "1px solid #f1f5f9",
                  boxShadow: "0 1px 3px 0 rgba(15, 23, 42, 0.05)",
                  backgroundColor: "#ffffff",
                  minHeight: 460,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", mb: 2 }}>
                  Recent Activity
                </Typography>

                {displayStats.recentJobs.length === 0 ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      flexGrow: 1,
                      py: 6,
                      px: 2,
                      textAlign: "center",
                    }}
                  >
                    <Avatar
                      sx={{
                        backgroundColor: "#f8fafc",
                        color: "#94a3b8",
                        width: 72,
                        height: 72,
                        mb: 2,
                      }}
                    >
                      <InboxIcon sx={{ fontSize: 36 }} />
                    </Avatar>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: "#334155", mb: 0.5 }}>
                      You're all caught up
                    </Typography>
                    <Typography variant="body2" color="textSecondary" sx={{ maxWidth: 320, mb: 3 }}>
                      No recent jobs are scheduled or updated for the selected period.
                    </Typography>
                    <Button
                      variant="outlined"
                      endIcon={<ArrowForwardIcon />}
                      onClick={() => navigate("/add-job")}
                      sx={{
                        borderRadius: "8px",
                        textTransform: "none",
                        fontWeight: 600,
                        color: "#3b82f6",
                        borderColor: "#e2e8f0",
                        "&:hover": {
                          borderColor: "#cbd5e1",
                          backgroundColor: "#f8fafc",
                        },
                      }}
                    >
                      Create New Job
                    </Button>
                  </Box>
                ) : (
                  <List sx={{ p: 0, flexGrow: 1 }}>
                    {displayStats.recentJobs.map((job, index) => (
                      <Box key={job.JobId}>
                        <ListItem
                          sx={{
                            px: 0.5,
                            py: 1.75,
                            borderRadius: "8px",
                            transition: "background-color 0.2s",
                            "&:hover": {
                              backgroundColor: "#f8fafc",
                            },
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 0.75 }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#1e293b" }}>
                                  {job.JobId}
                                </Typography>
                                <Chip
                                  label={job.subStatus ? getSubStatusLabel(job.subStatus) : job.status}
                                  color={getStatusColor(job.subStatus || job.status)}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "0.75rem",
                                    textTransform: "capitalize",
                                    height: 22,
                                  }}
                                />
                              </Box>
                            }
                            secondary={
                              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
                                <Box>
                                  <Typography variant="body2" sx={{ fontWeight: 500, color: "#475569" }}>
                                    {job.clientName || job.client}
                                  </Typography>
                                  <Typography variant="caption" sx={{ color: "#94a3b8" }}>
                                    {job.jobType || job.type}
                                  </Typography>
                                </Box>
                                <Typography variant="caption" sx={{ color: "#64748b", fontWeight: 500 }}>
                                  Due: {new Date(job.dueDate).toLocaleDateString("en-IN", {
                                    day: "numeric",
                                    month: "short",
                                    year: "numeric",
                                  })}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < displayStats.recentJobs.length - 1 && <Divider sx={{ borderColor: "#f1f5f9" }} />}
                      </Box>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>

            {/* Processing Jobs Pipeline Breakdown */}
            <Grid item xs={12} sm={6}>
              <Paper
                sx={{
                  p: 3,
                  borderRadius: "12px",
                  border: "1px solid #f1f5f9",
                  boxShadow: "0 1px 3px 0 rgba(15, 23, 42, 0.05)",
                  backgroundColor: "#ffffff",
                  minHeight: 460,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, color: "#1e293b", mb: 3 }}>
                  Job Pipeline Overview
                </Typography>

                <Box sx={{ mt: 1 }}>
                  <PipelineProgressRow
                    label="Accepted"
                    count={displayStats.processingJobs.accepted}
                    total={totalProcessing}
                    color="#10b981"
                    subStatusKey="accepted"
                  />
                  <PipelineProgressRow
                    label="Documents Pending"
                    count={displayStats.processingJobs.documentsPending}
                    total={totalProcessing}
                    color="#f59e0b"
                    subStatusKey="documentsPending"
                  />
                  <PipelineProgressRow
                    label="Tax Pending"
                    count={displayStats.processingJobs.taxPending}
                    total={totalProcessing}
                    color="#06b6d4"
                    subStatusKey="taxPending"
                  />
                  <PipelineProgressRow
                    label="Verification Pending"
                    count={displayStats.processingJobs.verificationPending}
                    total={totalProcessing}
                    color="#8b5cf6"
                    subStatusKey="verificationPending"
                  />
                  <PipelineProgressRow
                    label="Client Not Responding"
                    count={displayStats.processingJobs.clientNotResponding}
                    total={totalProcessing}
                    color="#ef4444"
                    subStatusKey="clientNotResponding"
                  />
                  <PipelineProgressRow
                    label="Kept on Hold"
                    count={displayStats.processingJobs.onHold}
                    total={totalProcessing}
                    color="#64748b"
                    subStatusKey="keptOnHold"
                  />
                </Box>

                <Divider sx={{ my: 3, borderColor: "#f1f5f9" }} />

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography variant="body2" sx={{ color: "#64748b", fontWeight: 500 }}>
                    Total in Pipeline
                  </Typography>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#0f172a" }}>
                    {totalProcessing} Active Job{totalProcessing !== 1 ? "s" : ""}
                  </Typography>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Grid>

        {/* RIGHT COLUMN: PINNED PERSISTENT LIVE JOB ACTIVITY PANEL */}
        <Grid item xs={12} md={4.5} lg={4}>
          <Box
            sx={{
              position: { xs: "static", md: "sticky" },
              top: 20,
              height: { xs: "520px", md: "calc(100vh - 140px)" },
              minHeight: { md: 520 },
              mb: { xs: 3, md: 0 },
            }}
          >
            <NormalLiveActivityFeed />
          </Box>
        </Grid>
      </Grid>

      {/* Report Generator Dialog */}
      <ReportGenerator open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} />
    </Box>
  )
}

export default NormalDashboard